import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { PublicLayout } from '@/components/layout/PublicLayout';
import { Button } from '@/components/ui/button';
import { getProducts, getCategories, getCMSContent } from '@/lib/api';
import SEO from '@/components/SEO';
import { ArrowRight, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';
import { getImageUrl } from '@/components/ImageUploadField';

// Optimise les URLs : resize MongoDB via ?w=, Unsplash via params URL
const optimizeImg = (url, width = 600) => {
  if (!url) return url;
  if (url.startsWith('http') && url.includes('/api/images/')) {
    return url.includes('?') ? url : `${url}?w=${width}`;
  }
  if (url.includes('images.unsplash.com')) {
    const base = url.replace(/&w=\d+/, '').replace(/&q=\d+/, '');
    return `${base}&w=${width}&q=75`;
  }
  return url;
};

// Default images for categories that don't have one
const DEFAULT_CATEGORY_IMAGES = {
  tapis: 'https://images.unsplash.com/photo-1600166898405-da9535204843?w=800&q=80',
  vases: 'https://images.unsplash.com/photo-1612196808214-b8e1d6145a8c?w=600&q=80',
  coussins: 'https://images.unsplash.com/photo-1555041469-a586c61ea9bc?w=600&q=80',
  luminaires: 'https://images.unsplash.com/photo-1513506003901-1e6a229e2d15?w=800&q=80',
  default: 'https://images.unsplash.com/photo-1618220179428-22790b461013?w=800&q=80',
};

const getCategoryImage = (category) => {
  if (category.image_url) return getImageUrl(category.image_url);
  const slug = category.slug?.toLowerCase() || '';
  return DEFAULT_CATEGORY_IMAGES[slug] || DEFAULT_CATEGORY_IMAGES.default;
};

const DEFAULT_CMS = {
  hero_title: 'Villa Marceau',
  hero_subtitle: 'Chic et Bohème',
  hero_description: 'Des pièces uniques et artisanales, invitation au voyage et à la découverte du savoir-faire.',
  hero_button_text: 'Découvrir la collection',
  hero_image: '/images/hero-bg-v2.jpeg',
  intro_title1: 'Un espace dédié',
  intro_title2: 'au beau',
  intro_text1: 'Niché au cœur de Tours, Villa Marceau est votre destination pour découvrir des produits d\'exception soigneusement sélectionnés pour vous.',
  intro_text2: 'Chaque pièce raconte une histoire, mêlant tradition artisanale et design contemporain pour sublimer votre intérieur.',
  intro_address: '26 rue Marceau, 37000 Tours',
  intro_image: '/images/centre-photo.jpeg',
  categories_title: 'Nos Collections',
  categories_subtitle: 'Explorez nos univers soigneusement composés',
  featured_title: 'Sélection du moment',
  featured_subtitle: 'Nos coups de cœur, choisis avec soin',
  cta_title: 'Visitez notre boutique',
  cta_text: 'Venez découvrir nos collections en personne et laissez-vous inspirer par notre sélection unique de tapis et accessoires de décoration.',
  cta_address: '26 rue Marceau, 37000 Tours',
};

export default function HomePage() {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cms, setCms] = useState(null); // null tant que le CMS n'a pas répondu — évite le double-download hero

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productsRes, categoriesRes, cmsRes] = await Promise.all([
          getProducts({ featured: true }),
          getCategories(),
          getCMSContent().catch(() => ({ data: {} }))
        ]);
        const products = (productsRes.data || []).slice(0, 4);
        setFeaturedProducts(products);
        setCategories(categoriesRes.data || []);
        // Fusion CMS serveur + valeurs par défaut — rendue seulement APRÈS la réponse.
        setCms({ ...DEFAULT_CMS, ...(cmsRes.data || {}) });
      } catch (error) {
        console.error('Error fetching data:', error);
        setCms(DEFAULT_CMS); // fallback si l'API échoue
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const displayCategories = categories.slice(0, 4);
  // Largeurs explicites : sans elles, serve_image renvoie l'original brut.
  // Hero 1600px, intro 800px — cf. audit LCP.
  const heroImage = cms ? getImageUrl(cms.hero_image, 1600) : null;
  const introImage = cms ? getImageUrl(cms.intro_image, 800) : null;
  // Textes : DEFAULT_CMS en attendant la réponse serveur (les valeurs sont identiques
  // à celles renvoyées par /api/cms — pas de flash de contenu).
  const c = cms || DEFAULT_CMS;

  return (
    <PublicLayout>
      <SEO
        title="Concept Store Bohème — Tapis & Décoration à Tours"
        description="Villa Marceau, concept store bohème à Tours. Découvrez notre sélection de tapis artisanaux, objets de décoration et accessoires d'intérieur soigneusement choisis."
        canonical="/"
      />
      {/* Hero Section */}
      <section className="relative h-[90vh] flex items-center justify-center overflow-hidden grain-overlay bg-stone-100">
        {/* Vraie <img> plutôt qu'un background-image : le préchargeur du navigateur
            ne voit jamais l'URL d'un fond CSS (connue seulement après bundle + React + /api/cms).
            fetchPriority=high + décodage async + pas de lazy = LCP optimisé. */}
        {heroImage && (
          <img
            src={heroImage}
            alt=""
            aria-hidden="true"
            fetchPriority="high"
            decoding="async"
            className="absolute inset-0 w-full h-full object-cover hero-bg-image"
          />
        )}

        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-foreground/70 text-sm tracking-[0.3em] uppercase mb-4">
              Concept Store à Tours
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-bold text-foreground mb-6 leading-tight">
              {c.hero_title}<br />
              <span className="italic font-normal text-3xl sm:text-4xl md:text-5xl lg:text-6xl">{c.hero_subtitle}</span>
            </h1>
            <p className="text-foreground/80 text-base sm:text-lg md:text-xl max-w-2xl mx-auto mb-10 leading-relaxed">
              {c.hero_description}
            </p>
            <div className="flex justify-center">
              <Link to="/commander-en-ligne" data-testid="hero-catalogue-btn">
                <Button className="btn-primary bg-foreground text-white hover:bg-foreground/90">
                  {c.hero_button_text}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2">
          <div className="w-6 h-10 border-2 border-foreground/30 rounded-full flex justify-center">
            <div className="w-1 h-3 bg-foreground/50 rounded-full mt-2 animate-bounce" />
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-20 md:py-32 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="font-heading text-4xl md:text-5xl font-semibold mb-6 leading-tight">
                {c.intro_title1}<br />
                <span className="text-primary">{c.intro_title2}</span>
              </h2>
              <p className="text-muted-foreground text-lg leading-relaxed mb-6">
                {c.intro_text1}
              </p>
              <p className="text-muted-foreground text-lg leading-relaxed mb-8">
                {c.intro_text2}
              </p>
              <div className="flex items-center gap-2 text-primary">
                <Sparkles className="w-5 h-5" />
                <span className="font-medium">{c.intro_address}</span>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="aspect-[4/5] rounded-md overflow-hidden bg-muted">
                {introImage && (
                  <img
                    src={introImage}
                    alt="Intérieur bohème élégant"
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover"
                  />
                )}
              </div>
              <div className="absolute -bottom-6 -left-6 w-48 h-48 bg-primary/10 rounded-md -z-10" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Dynamic Categories Grid */}
      <section className="py-20 px-6 lg:px-12 bg-muted/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading text-4xl md:text-5xl font-semibold mb-4">
              {c.categories_title}
            </h2>
            <p className="text-muted-foreground text-lg">
              {c.categories_subtitle}
            </p>
          </div>

          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {[...Array(4)].map((_, i) => (
                <div key={i} className={`${i === 0 ? 'col-span-2 row-span-2' : ''} aspect-square bg-muted rounded-md animate-pulse`} />
              ))}
            </div>
          ) : displayCategories.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {displayCategories.map((category, index) => {
                const isLarge = index === 0;
                const isWide = index === 3 && displayCategories.length === 4;
                return (
                  <motion.div
                    key={category.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className={`${isLarge ? 'col-span-2 row-span-2' : ''} ${isWide ? 'col-span-2' : ''}`}
                  >
                    <Link
                      to={`/commander-en-ligne?category=${category.id}`}
                      className={`group block relative overflow-hidden rounded-md ${
                        isLarge ? 'aspect-square' : isWide ? 'aspect-[2/1]' : 'aspect-square'
                      }`}
                      data-testid={`category-${category.slug}`}
                    >
                      <img
                        src={optimizeImg(getCategoryImage(category), isLarge ? 800 : 400)}
                        alt={category.name}
                        loading={index < 2 ? "eager" : "lazy"}
                        decoding="async"
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                      <div className={`absolute ${isLarge ? 'bottom-6 left-6 right-6' : 'bottom-4 left-4'}`}>
                        <h3 className={`font-heading font-semibold text-white ${
                          isLarge ? 'text-3xl md:text-4xl mb-2' : 'text-xl'
                        }`}>
                          {category.name}
                        </h3>
                        {isLarge && category.description && (
                          <p className="text-white/80 text-sm line-clamp-2">
                            {category.description}
                          </p>
                        )}
                      </div>
                    </Link>
                  </motion.div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-muted-foreground">Aucune catégorie disponible</p>
            </div>
          )}
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-20 md:py-32 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12">
            <div>
              <h2 className="font-heading text-4xl md:text-5xl font-semibold mb-4">
                {c.featured_title}
              </h2>
              <p className="text-muted-foreground text-lg">
                {c.featured_subtitle}
              </p>
            </div>
            <Link
              to="/commander-en-ligne"
              className="btn-secondary inline-flex items-center mt-6 md:mt-0"
              data-testid="view-all-products"
            >
              Voir tout
              <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="aspect-[3/4] bg-muted rounded-md mb-4" />
                  <div className="h-4 bg-muted rounded w-3/4 mb-2" />
                  <div className="h-4 bg-muted rounded w-1/2" />
                </div>
              ))}
            </div>
          ) : featuredProducts.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {featuredProducts.map((product, index) => (
                <motion.div
                  key={product.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Link
                    to={`/produit/${product.slug}`}
                    className="group product-card block"
                    data-testid={`product-card-${product.slug}`}
                  >
                    <div className="aspect-[3/4] overflow-hidden rounded-md mb-4 bg-muted">
                      <img
                        src={optimizeImg(getImageUrl(product.images?.[0])) || DEFAULT_CATEGORY_IMAGES.default}
                        alt={product.name}
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <h3 className="font-heading text-lg font-medium mb-1 group-hover:text-primary transition-colors duration-300">
                      {product.name}
                    </h3>
                    <p className="text-primary font-semibold">
                      {typeof product.price === 'number' ? product.price.toFixed(2) : '—'} €
                    </p>
                  </Link>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-muted-foreground mb-4">
                Aucun produit vedette pour le moment.
              </p>
              <Link to="/commander-en-ligne" className="btn-primary inline-block mt-4">
                Commander en ligne
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Gallery Carousel supprimé */}

      {/* CTA Section */}
      <section className="py-20 px-6 lg:px-12 bg-stone-800 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-4xl md:text-5xl font-semibold mb-6">
            {c.cta_title}
          </h2>
          <p className="text-white/80 text-lg mb-8 leading-relaxed">
            {c.cta_text}
          </p>
          <div className="inline-flex items-center gap-2 text-primary-foreground bg-white/10 backdrop-blur px-6 py-3 rounded-full">
            <Sparkles className="w-5 h-5 text-amber-400" />
            <span className="font-medium">{c.cta_address}</span>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
